import { useCart } from '@shopify/hydrogen-react';
import { Cart as StorefrontCart } from '@shopify/hydrogen-react/storefront-api-types';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../../common/formatCurrencies';
import { NotificationMessages } from '../../common/notificationMessages';
import Button, { ButtonSize, ButtonVariation } from '../../components/ui/Button';
import FlashMessages from '../../components/ui/FlashMessages';

const DiscountCode: React.FC = () => {
  const cart = useCart();
  const { discountCodes, discountCodesUpdate, status } = cart;
  const discountAllocations = (cart as Partial<StorefrontCart>).discountAllocations ?? [];
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingCode || status === 'updating') return;

    const landed = discountCodes?.find(d => d?.code?.toLowerCase() === pendingCode.toLowerCase());

    if (!landed || landed.applicable === false) {
      setError(`"${pendingCode}" isn't valid.`);
    } else {
      setError(null);
      setInput('');
      FlashMessages.success(NotificationMessages.CART_UPDATED);
    }

    setPendingCode(null);
    setSubmitting(false);
  }, [status, pendingCode, discountCodes]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const code = input.trim();
    if (!code || submitting) return;

    setError(null);
    setSubmitting(true);
    setPendingCode(code);

    const existing = (discountCodes ?? [])
      .map(d => d?.code)
      .filter((c): c is string => !!c && c.toLowerCase() !== code.toLowerCase());

    discountCodesUpdate?.([...existing, code]);
  };

  const remove = (code: string) => {
    const next = (discountCodes ?? [])
      .map(d => d?.code)
      .filter((c): c is string => !!c && c.toLowerCase() !== code.toLowerCase());

    if (input.trim().toLowerCase() === code.toLowerCase()) {
      setInput('');
    }
    setError(null);
    setPendingCode(null);
    discountCodesUpdate?.(next);
  };

  const totalDiscount = (discountAllocations ?? []).reduce(
    (sum, a) => sum + (a.discountedAmount?.amount ? +a.discountedAmount.amount : 0),
    0
  );

  return (
    <div className="discount-code form">
      <form onSubmit={apply}>
        <div className="form-group discount-code__row">
          <div className="form-field">
            <input
              aria-label="Promo code"
              className={classNames('small', { error: !!error })}
              disabled={submitting}
              name="discountCode"
              onChange={e => setInput(e.target.value)}
              placeholder="Promo code"
              type="text"
              value={input}
            />
          </div>
          <Button
            type="submit"
            variation={ButtonVariation.PRIMARY_OUTLINED}
            size={ButtonSize.SMALL}
            disabled={submitting || !input.trim()}
          >
            {submitting ? 'Applying…' : 'Apply'}
          </Button>
        </div>
        {error && <div className="note">{error}</div>}
      </form>

      {!!discountCodes?.length && (
        <ul className="discount-code__applied">
          {discountCodes.map(d => (
            <li key={d?.code} className="discount-code__chip">
              <span>{d?.code}</span>
              {d?.applicable === false && (
                <span className="discount-code__chip-invalid"> (not applicable)</span>
              )}
              <button
                type="button"
                onClick={() => d?.code && remove(d.code)}
                aria-label={`Remove ${d?.code}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {totalDiscount > 0 && (
        <div className="discount-code__savings">You save {formatCurrency(totalDiscount)}</div>
      )}
    </div>
  );
};

export default DiscountCode;
